import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { CartItem } from '@/types';
import { useTranslation } from 'react-i18next';

interface GeneralItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartItem: CartItem | null;
  onSave: (newName: string, costPrice: number, salePrice: number, isService: boolean) => void;
}

export default function GeneralItemModal({ isOpen, onClose, cartItem, onSave }: GeneralItemModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [costPrice, setCostPrice] = useState<number | ''>('');
  const [salePrice, setSalePrice] = useState<number | ''>('');
  const [isService, setIsService] = useState(false);

  useEffect(() => {
    if (isOpen && cartItem) {
      setName(cartItem.item_name || 'General Item');
      setCostPrice(cartItem.cost_price || '');
      setSalePrice(cartItem.rate || '');
      setIsService(cartItem.item_type === 'Service');
    }
  }, [isOpen, cartItem]);

  if (!isOpen || !cartItem) return null;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (salePrice === '' || salePrice <= 0) return;
    
    const finalCost = costPrice === '' ? 0 : Number(costPrice);
    onSave(name.trim(), finalCost, Number(salePrice), isService);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4 sm:p-0">
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]"
        role="dialog"
        aria-modal="true"
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit General Item
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 overflow-y-auto">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('pos.itemName', 'Item Name')} *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Custom item name"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('pos.salePrice', 'Sale Price')} *
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('pos.costPrice', 'Cost Price')} <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="If empty, it matches Sale Price"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leaving this empty will record a profit of 0.
              </p>
            </div>

            <div className="pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isService}
                  onChange={(e) => setIsService(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                />
                <span className="text-sm font-medium text-gray-900">
                  {t('pos.treatAsService', 'Treat as a Service')}
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 pl-6">
                {t('pos.treatAsServiceHint', 'Check this if the item requires staff assignment or an appointment.')}
              </p>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim() || salePrice === '' || salePrice <= 0}
              className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {t('common.save', 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
